"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Max height on mobile. Default: "90vh" */
  maxHeight?: string;
  /** Z-index for the overlay. Default: 300 */
  zIndex?: number;
  /** Hide close button (e.g. when header has its own). Default: false */
  hideCloseButton?: boolean;
  /** Additional className for the content container */
  className?: string;
}

const CLOSE_DURATION = 250;
const SWIPE_THRESHOLD = 80;

export default function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  maxHeight = "90vh",
  zIndex = 300,
  hideCloseButton = false,
  className = "",
}: BottomSheetProps) {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  const isDragging = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useBodyScrollLock(isOpen && !closing);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setDragY(0);
      onClose();
    }, CLOSE_DURATION);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, handleClose]);

  // Touch handlers for swipe-to-dismiss (mobile only)
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    // Only allow drag from the top area (drag handle + header)
    const touch = e.touches[0];
    const rect = contentRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Only initiate drag if touching the top 60px of the modal
    if (touch.clientY - rect.top > 60) return;
    dragStartY.current = touch.clientY;
    isDragging.current = true;
    setDragY(0);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    // Only allow dragging down
    if (delta > 0) {
      setDragY(delta);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (dragY > SWIPE_THRESHOLD) {
      handleClose();
    } else {
      setDragY(0);
    }
  }, [dragY, handleClose]);

  if (!mounted || !isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 flex items-end md:items-center justify-center"
      style={{ zIndex }}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-250 ${
          closing ? "opacity-0" : "opacity-100"
        }`}
        onClick={handleClose}
      />

      {/* Modal content.
          Default desktop width is `md:max-w-md` (448px). Consumers can
          override by passing a `md:max-w-*` class via `className`, but
          Tailwind's CSS doesn't guarantee a winning declaration when
          two same-property utilities sit in the same class string —
          we'd sometimes ship the 4xl modal at 448px. So strip our
          default whenever the consumer-supplied className contains
          a `md:max-w-` utility, leaving theirs as the only one in
          play. */}
      <div
        ref={contentRef}
        role="dialog"
        className={`relative bg-white dark:bg-[#18181D] rounded-t-[5px] md:rounded-[5px] border-t md:border border-gray-200 dark:border-[#1A1A1A] shadow-2xl w-full ${
          /\bmd:max-w-/.test(className) ? "" : "md:max-w-md"
        } flex flex-col overflow-hidden ${
          closing
            ? "animate-slide-down md:animate-modal-scale-out"
            : "animate-slide-up md:animate-modal-scale-in"
        } ${className}`}
        style={{
          maxHeight,
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging.current ? "none" : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Mobile drag handle */}
        <div className="flex md:hidden justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-[5px] bg-gray-300 dark:bg-gray-600" />
        </div>

        {/* Header (if title provided) */}
        {title && (
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-[#1A1A1A]">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            {!hideCloseButton && (
              <button
                onClick={handleClose}
                className="p-2 -mr-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded-[5px] transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>
        )}

        {/* Close button without title */}
        {!title && !hideCloseButton && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-[5px] transition-colors md:top-3 md:right-3"
          >
            <FiX className="w-5 h-5 text-gray-400" />
          </button>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
